import { useMemo, useState } from "react";
import { ArrowRight, CalendarDays, CircleDollarSign, Globe2, Languages, MessageCircle, Search, ShieldCheck, UsersRound } from "lucide-react";
import Globe from "./components/Globe";
import MetricCard from "./components/MetricCard";
import ProgramDashboard from "./components/ProgramDashboard";
import RelationshipMap from "./components/RelationshipMap";
import ResourceLibrary from "./components/ResourceLibrary";
import ScenarioLab from "./components/ScenarioLab";
import { CountryId, countries, programTotals } from "./data";
import { defaultScenario, formatCurrency, formatNumber, scenarioFromCountry } from "./lib/scenario";

function App() {
  const [selectedId, setSelectedId] = useState<CountryId>("uganda");
  const [hoveredId, setHoveredId] = useState<CountryId | null>(null);
  const [scenario, setScenario] = useState(defaultScenario);

  const selectedCountry = useMemo(() => countries.find((country) => country.id === selectedId) ?? countries[0], [selectedId]);
  const hoverCountry = useMemo(() => countries.find((country) => country.id === hoveredId) ?? null, [hoveredId]);
  const panelCountry = hoverCountry ?? selectedCountry;

  const selectCountry = (id: CountryId) => {
    setSelectedId(id);
    const country = countries.find((item) => item.id === id);
    if (country) setScenario(scenarioFromCountry(country));
  };

  return (
    <div className="app-shell">
      <header className="site-header">
        <a className="brand" href="#top" aria-label="Namuli Labs home">
          <img src="/assets/namuli_mark.svg" alt="" />
          <span>
            <strong>Namuli Labs</strong>
            <small>Evidence . Language . Impact</small>
          </span>
        </a>
        <nav aria-label="Primary navigation">
          <a href="#network">Network</a>
          <a href="#evidence">Evidence</a>
          <a href="#scenario">Scenario Lab</a>
          <a href="#program">Program</a>
          <a href="#resources">Resources</a>
          <a href="/messages">Messages</a>
        </nav>
        <div className="header-actions">
          <button type="button" aria-label="Search resources"><Search size={17} /></button>
          <a className="domain-link" href="https://namulilabs.com" target="_blank" rel="noreferrer">namulilabs.com</a>
          <a className="portal-link" href="/messages">Messages <MessageCircle size={14} /></a>
        </div>
      </header>

      <main id="top">
        <section className="hero-section">
          <div className="hero-copy">
            <h1>Namuli Access</h1>
            <p>
              Inclusive public-service language access for communities that need trusted local-language guidance, governed AI, field validation and evidence-driven policy adoption.
            </p>
            <div className="hero-actions">
              <a href="#evidence" className="primary-action">Explore the program <ArrowRight size={17} /></a>
              <a href="#scenario" className="secondary-action">Run scenario analysis</a>
            </div>
            <div className="metric-grid hero-metrics">
              <MetricCard label="Country pilot" value={String(programTotals.pilotCountries)} detail="Uganda, Mozambique" icon={<Globe2 />} tone="gold" />
              <MetricCard label="Language tracks" value={String(programTotals.pilotLanguages)} detail="Full packs and readiness briefs" icon={<Languages />} tone="teal" />
              <MetricCard label="Stage 1 model" value={formatCurrency(programTotals.stageOneBudget, true)} detail="FID pilot grant" icon={<CircleDollarSign />} tone="gold" />
              <MetricCard label="Pilot duration" value={String(programTotals.durationMonths)} detail="Months" icon={<CalendarDays />} tone="green" />
            </div>
          </div>

          <div className="hero-network">
            <Globe countries={countries} selectedId={selectedId} onSelect={selectCountry} onHover={setHoveredId} />
            <div className="country-control">
              <label htmlFor="country-focus">Focus country</label>
              <select id="country-focus" value={selectedId} onChange={(event) => selectCountry(event.currentTarget.value as CountryId)}>
                {countries.map((country) => <option key={country.id} value={country.id}>{country.name}</option>)}
              </select>
            </div>
            <div className="country-labels" aria-label="Country network labels">
              {countries.map((country) => (
                <button
                  key={country.id}
                  type="button"
                  className={`${country.id} ${country.id === selectedId ? "active" : ""}`}
                  onClick={() => selectCountry(country.id)}
                >
                  <i />
                  {country.name}
                </button>
              ))}
            </div>
            <aside className="globe-inspector">
              <span className={panelCountry.role === "Pilot country" ? "status pilot" : "status regional"}>{panelCountry.role}</span>
              <h2>{panelCountry.name}</h2>
              <dl>
                <div><dt>Languages</dt><dd>{panelCountry.languages.length + panelCountry.readinessBriefs.length}</dd></div>
                <div><dt>Communities</dt><dd>{formatNumber(panelCountry.communities)}</dd></div>
                <div><dt>Validators</dt><dd>{formatNumber(panelCountry.validators)}</dd></div>
                <div><dt>Service journeys</dt><dd>{panelCountry.serviceJourneys}</dd></div>
                <div><dt>Field sites</dt><dd>{panelCountry.fieldSites}</dd></div>
                <div><dt>Phrase target</dt><dd>{formatNumber(panelCountry.phraseTarget)}</dd></div>
              </dl>
              <button type="button" onClick={() => selectCountry(panelCountry.id)}>View country dashboard</button>
            </aside>
            <div className="route-legend" aria-label="Map legend">
              <span><i className="legend-pilot" />Pilot country</span>
              <span><i className="legend-regional" />Regional pipeline</span>
              <span><i className="legend-route" />Knowledge route</span>
            </div>
          </div>
        </section>

        <ProgramDashboard selected={selectedCountry} onSelect={selectCountry} />

        <section id="program" className="section program-section">
          <div className="section-heading">
            <div>
              <span className="section-index">Program</span>
              <h2>Country Operating Model</h2>
            </div>
            <p>Each country program runs through a governed sequence from source approval to language validation, safe release and field issue triage.</p>
          </div>
          <div className="operating-model">
            {[
              ["1", "Governance", "Lead applicant, fiscal host route, data protection, safeguarding and release controls."],
              ["2", "Source mapping", "Public-service source library, service-journey metadata and source-update cadence."],
              ["3", "Language validation", "Language leads, reviewers and community validators test terminology, comprehension and fit."],
              ["4", "Product release", "Retrieval, ranking, source-traced answers, refusal logic and country release gate."],
              ["5", "MEL and scale", "Task testing, survey waves, interviews, focus groups, cost evidence and adoption pathway."],
            ].map(([step, title, body]) => (
              <div key={step} className="model-step">
                <strong>{step}</strong>
                <h3>{title}</h3>
                <p>{body}</p>
              </div>
            ))}
          </div>
          <div className="country-language-panels">
            {[countries[0], countries[1]].map((country) => (
              <article key={country.id}>
                <h3>{country.name}</h3>
                <p>{country.baselineNeed}</p>
                <div>
                  <span>Primary languages</span>
                  <strong>{country.languages.join("; ")}</strong>
                </div>
                <div>
                  <span>Readiness briefs</span>
                  <strong>{country.readinessBriefs.join("; ")}</strong>
                </div>
              </article>
            ))}
          </div>
        </section>

        <RelationshipMap />
        <ScenarioLab scenario={scenario} setScenario={setScenario} />
        <ResourceLibrary />
      </main>

      <footer className="site-footer">
        <div className="brand footer-brand">
          <img src="/assets/namuli_mark.svg" alt="" />
          <span>
            <strong>Namuli Labs</strong>
            <small>Evidence . Language . Impact</small>
          </span>
        </div>
        <p>Namuli Access is a product of Namuli Labs. Copyright 2026 Leopoldino Jeronimo. All rights reserved.</p>
        <div className="footer-proof">
          <span>namulilabs.com</span>
          <span><MessageCircle size={16} /> Protected messages</span>
          <span><ShieldCheck size={16} /> Data protection by design</span>
          <span><UsersRound size={16} /> Country-led implementation</span>
        </div>
      </footer>
    </div>
  );
}

export default App;
